import {useCallback, useEffect, useState} from "react";
import {
  listAutonomousPolicies, listAutonomousWork, readAutonomousDashboard,
  previewEffectivePolicy, type AutonomousDashboard, type AutonomousEffectivePolicy,
  type AutonomousPolicy, type AutonomousWorkRequest
} from "../../api/autonomousFactory";
import type {AuthorizedFetch} from "../../api/client";
import {useAuth} from "../../auth/AuthContext";
import {AsyncState} from "../../components/AsyncState";
import {PageHeader} from "../../components/PageHeader";
import {flowTraceIdFromMap} from "../../utils/flowTrace";

export type AutonomousFactoryView = "ORCHESTRATION" | "ACTION_CENTER" | "POLICY" | "DELIVERY";

export function AutonomousFactory({view}: {view: AutonomousFactoryView}) {
  const {authorizedFetch} = useAuth();
  const [dashboard, setDashboard] = useState<AutonomousDashboard | null>(null);
  const [work, setWork] = useState<AutonomousWorkRequest[] | null>(null);
  const [policies, setPolicies] = useState<AutonomousPolicy[] | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [nextDashboard, nextWork, nextPolicies] = await Promise.all([
        readAutonomousDashboard(authorizedFetch, signal),
        listAutonomousWork(authorizedFetch, signal),
        listAutonomousPolicies(authorizedFetch, signal)
      ]);
      setDashboard(nextDashboard); setWork(nextWork); setPolicies(nextPolicies); setError("");
    } catch (caught) {
      if (!signal?.aborted) setError(caught instanceof Error ? caught.message : "Governed orchestration data is unavailable");
    }
  }, [authorizedFetch]);
  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);

  const copy = header(view);
  return <>
    <PageHeader eyebrow={copy.eyebrow} title={copy.title} description={copy.description}
      aside={<button type="button" className="secondary" onClick={() => void load()}>Refresh</button>} />
    <section className="panel page-panel autonomous-factory-page">
      <p className="safety-banner" role="note"><strong>Governed preparation only.</strong> AI cannot approve,
        merge, release, deploy, activate policy, restore, delete backups, or satisfy a human gate.</p>
      {error && <AsyncState kind="error" detail={error} />}
      {!error && (!dashboard || !work || !policies) && <AsyncState kind="loading" detail="Reading persisted orchestration, policy, escalation, and delivery projections." />}
      {dashboard && work && policies && <ViewContent view={view} dashboard={dashboard} work={work}
        policies={policies} authorizedFetch={authorizedFetch} />}
    </section>
  </>;
}

function ViewContent({view, dashboard, work, policies, authorizedFetch}: {
  view: AutonomousFactoryView;
  dashboard: AutonomousDashboard;
  work: AutonomousWorkRequest[];
  policies: AutonomousPolicy[];
  authorizedFetch: AuthorizedFetch;
}) {
  if (view === "ACTION_CENTER") return <ActionCenter dashboard={dashboard} work={work} />;
  if (view === "POLICY") return <PolicyAdministration policies={policies} authorizedFetch={authorizedFetch} />;
  if (view === "DELIVERY") return <DeliveryIntelligence work={work} dashboard={dashboard} />;
  return <OrchestrationOverview dashboard={dashboard} work={work} />;
}

function OrchestrationOverview({dashboard, work}: {dashboard: AutonomousDashboard; work: AutonomousWorkRequest[]}) {
  return <>
    <div className="autonomous-summary" aria-label="Autonomous orchestration summary">
      <Metric label="Governed requests" value={String(work.length)} />
      <Metric label="Waiting for human" value={String(dashboard.waitingHumanGates)} />
      <Metric label="Open actions" value={String(dashboard.actionItems.length)} />
      <Metric label="Active policies" value={String(dashboard.activePolicies.length)} />
    </div>
    <section aria-labelledby="factory-work-title"><h2 id="factory-work-title">Work orchestration</h2>
      {work.length === 0 ? <Empty text="No approved work has entered autonomous preparation in this scope." />
        : <div className="autonomous-table-scroll"><table><thead><tr><th>Work</th><th>Class</th><th>Complexity / risk</th><th>State</th><th>Authority</th></tr></thead>
          <tbody>{work.map(item => <tr key={item.id}><td><strong>{item.requestKey}</strong><small>{item.requestedOutcome}</small>{flowTraceIdFromMap(item.contextAuthority) && <small><a href={`/flow-traces/${encodeURIComponent(flowTraceIdFromMap(item.contextAuthority)!)}`}>Flow {flowTraceIdFromMap(item.contextAuthority)}</a></small>}</td>
            <td>{human(item.taskClass)}</td><td>{item.complexity} / {item.risk}</td><td><State value={item.status} /></td>
            <td>{item.approvingAuthority}</td></tr>)}</tbody></table></div>}
    </section>
    <DecisionFlow />
  </>;
}

function ActionCenter({dashboard, work}: {dashboard: AutonomousDashboard; work: AutonomousWorkRequest[]}) {
  return <section aria-labelledby="action-title"><h2 id="action-title">Attention required</h2>
    {dashboard.actionItems.length === 0 ? <Empty text="No escalations currently require governed human attention." />
      : <ul className="autonomous-card-list">{dashboard.actionItems.map((item, index) => {
        const request = work.find(candidate => candidate.id === item.requestId);
        const flowTraceId = flowTraceIdFromMap(request?.contextAuthority);
        return <li key={String(item.id ?? index)}>
        <strong>{String(item.title ?? "Governed action")}</strong><span>{String(item.requiredAuthority ?? "Named human")}</span>
        <small>{String(item.escalationLevel ?? item.level ?? "E3")} · {String(item.blockerType ?? "HUMAN_GATE_BLOCKER")}</small>
        {flowTraceId && <small><a href={`/flow-traces/${encodeURIComponent(flowTraceId)}`}>Flow {flowTraceId}</a></small>}
        {Boolean(item.evidenceReference) && <a href={String(item.evidenceReference)}>Open governed evidence</a>}</li>;
      })}</ul>}
    <h2>Escalation posture</h2><dl className="autonomous-definition-grid">
      {Object.entries(dashboard.openEscalations).map(([level, total]) => <div key={level}><dt>{level}</dt><dd>{total}</dd></div>)}
    </dl>
  </section>;
}

function PolicyAdministration({policies, authorizedFetch}: {policies: AutonomousPolicy[]; authorizedFetch: AuthorizedFetch}) {
  const [preview, setPreview] = useState<AutonomousEffectivePolicy | null>(null);
  const [previewError, setPreviewError] = useState("");
  const previewPolicy = async () => {
    try {
      const platform = policies.find(policy => policy.scopeType === "PLATFORM_DEFAULT");
      setPreview(await previewEffectivePolicy(authorizedFetch,
        {PLATFORM_DEFAULT: platform?.scopeKey ?? "PLATFORM"}));
      setPreviewError("");
    } catch (caught) {
      setPreviewError(caught instanceof Error ? caught.message : "Effective policy preview failed");
    }
  };
  return <>
    <div className="policy-principle"><strong>AI recommends.</strong><strong>Governed policy decides.</strong><strong>Human authority remains where required.</strong></div>
    <section aria-labelledby="policy-title"><h2 id="policy-title">Versioned effective policy</h2>
      {policies.length === 0 ? <Empty text="No autonomous policy versions exist in this scope." />
        : <ul className="autonomous-card-list">{policies.map(policy => <li key={policy.id}><strong>{policy.key} v{policy.version}</strong>
          <span><State value={policy.status} /> · {human(policy.scopeType)} / {policy.scopeKey}</span>
          <small>{policy.profile} · author {policy.authorSubject}{policy.approverSubject ? ` · approver ${policy.approverSubject}` : " · independent approval pending"}</small></li>)}</ul>}
    </section>
    <section aria-labelledby="policy-tools-title"><h2 id="policy-tools-title">Policy tools</h2>
      <button type="button" className="secondary" onClick={() => void previewPolicy()}>Preview effective policy</button>
      {previewError && <p role="alert">{previewError}</p>}
      {preview && <div className="effective-policy-preview" role="status"><strong>{preview.profile}</strong>
        <span>{preview.matchedPolicyIds.length} matched version{preview.matchedPolicyIds.length === 1 ? "" : "s"}</span>
        <code>{preview.resolutionSha256}</code><small>Read-only preview · no policy activation</small></div>}
      <div className="autonomous-capability-grid"><Capability title="Effective policy preview" text="Explain matched, overridden, rejected, and immutable rules before execution." />
        <Capability title="Simulation and backtesting" text="Compare current, suggested, and administrator-modified drafts without activation." />
        <Capability title="Load Suggested Configuration" text="Loads selected AI recommendations into an editable Draft Policy Version only." />
        <Capability title="Rollback and audit" text="Preserves version lineage, reason, author, independent approver, and rollback target." /></div>
    </section>
  </>;
}

function DeliveryIntelligence({work, dashboard}: {work: AutonomousWorkRequest[]; dashboard: AutonomousDashboard}) {
  return <>
    <div className="autonomous-summary"><Metric label="Tracked work" value={String(work.length)} />
      <Metric label="Human-gate demand" value={String(dashboard.waitingHumanGates)} />
      <Metric label="Policy baselines" value={String(dashboard.activePolicies.length)} />
      <Metric label="Forecast mode" value="Advisory" /></div>
    <section aria-labelledby="delivery-title"><h2 id="delivery-title">Delivery impact intelligence</h2>
      <div className="autonomous-capability-grid"><Capability title="Time and critical path" text="Planning, execution, testing, remediation, queue, and human-review time with confidence." />
        <Capability title="Cost and budget" text="Local compute, provider, CI, storage, network, remediation, and human effort remain visible." />
        <Capability title="Capacity and bottlenecks" text="Worker, model, memory, CI, database, browser, storage, and human-review demand." />
        <Capability title="Estimate versus actual" text="Historical estimates remain immutable while variance improves future forecasts." /></div>
    </section>
    <section aria-labelledby="delivery-evidence-title"><h2 id="delivery-evidence-title">Forecast and actual evidence</h2>
      {dashboard.deliveryImpacts.length === 0 ? <Empty text="No immutable delivery forecast has been recorded for this scope." />
        : <div className="autonomous-table-scroll"><table><thead><tr><th>Work</th><th>Estimate</th><th>Actual</th><th>Cost</th><th>Confidence</th></tr></thead>
          <tbody>{dashboard.deliveryImpacts.map(impact => <tr key={impact.requestId}><td>{impact.requestKey}</td>
            <td>{impact.forecast.estimatedMinutes} min</td><td>{impact.actual ? `${impact.actual.actualMinutes} min` : "Pending"}</td>
            <td>{impact.forecast.estimatedCost}{impact.actual ? ` → ${impact.actual.actualCost}` : ""}</td>
            <td>{impact.forecast.confidence}</td></tr>)}</tbody></table></div>}
    </section>
    <section aria-labelledby="scenario-title"><h2 id="scenario-title">Scenario comparison</h2>
      <Empty text="Run a non-mutating policy simulation to compare current routing, local-first, quality-first, budget, capacity, or deadline scenarios." />
    </section>
  </>;
}

function DecisionFlow() {
  const stages = ["Approved intake", "Grounded context", "Dependency plan", "Worker role", "Model binding", "Resource admission", "Tests and evidence", "Human gate"];
  return <section aria-labelledby="decision-flow-title"><h2 id="decision-flow-title">Explainable decision flow</h2>
    <ol className="autonomous-flow">{stages.map((stage, index) => <li key={stage}><span>{index + 1}</span>{stage}</li>)}</ol></section>;
}

function Metric({label, value}: {label: string; value: string}) { return <div><strong>{value}</strong><span>{label}</span></div>; }
function State({value}: {value: string}) { return <span className="autonomous-state">{human(value)}</span>; }
function Capability({title, text}: {title: string; text: string}) { return <article><h3>{title}</h3><p>{text}</p></article>; }
function Empty({text}: {text: string}) { return <div className="empty-state"><h3>Nothing to show yet</h3><p>{text}</p></div>; }
function human(value: string) { return value.toLowerCase().replaceAll("_", " ").replace(/^./, letter => letter.toUpperCase()); }

function header(view: AutonomousFactoryView) {
  if (view === "ACTION_CENTER") return {eyebrow: "Governance / Action Center", title: "Action Center", description: "Prioritized blockers, escalations, required authority, and direct links to immutable execution evidence."};
  if (view === "POLICY") return {eyebrow: "Administration / Policy", title: "Orchestration policy", description: "Version, simulate, explain, approve, activate, and audit governed routing and escalation policy."};
  if (view === "DELIVERY") return {eyebrow: "Delivery Intelligence", title: "Delivery impact", description: "Forecast time, cost, capacity, risk, critical path, bottlenecks, and human-review demand."};
  return {eyebrow: "Execution Center / Autonomous Factory", title: "Autonomous orchestration", description: "Plan and explain governed worker-first delivery through a named-human consequential gate."};
}
